import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext(null);

export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    try {
      return localStorage.getItem('savvi-theme-mode') || THEME_MODES.SYSTEM;
    } catch {
      return THEME_MODES.SYSTEM;
    }
  });

  const getSystemPreference = () => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? THEME_MODES.DARK
        : THEME_MODES.LIGHT;
    }
    return THEME_MODES.LIGHT;
  };

  const applyTheme = (mode) => {
    const root = document.documentElement;
    const resolvedTheme =
      mode === THEME_MODES.SYSTEM ? getSystemPreference() : mode;
    if (resolvedTheme === THEME_MODES.DARK) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  };

  useEffect(() => {
    applyTheme(themeMode);

    let mediaQuery;
    if (themeMode === THEME_MODES.SYSTEM && window.matchMedia) {
      mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => applyTheme(THEME_MODES.SYSTEM);
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [themeMode]);

  const setTheme = (mode) => {
    try {
      localStorage.setItem('savvi-theme-mode', mode);
    } catch {}
    setThemeMode(mode);
  };

  const resolvedTheme =
    themeMode === THEME_MODES.SYSTEM ? getSystemPreference() : themeMode;

  return (
    <ThemeContext.Provider value={{ themeMode, setTheme, resolvedTheme, THEME_MODES }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
